// Global Instructions Rule Applied!
// Shape of GET /fund-transactions/fund/:fundId/fee-position
// (fee__booking_position): fund terms strip + one row per commitment that
// holds an interest on the date the fees are charged, comparing fees due
// (engine, booking-rounded) against fees booked (Booked-status
// management-fee journals), split prior/current at the selected period
// start. After a Transfer of Interest the rows follow that date: the
// transferees on or after the transfer, the transferor before it.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FeeBookingCrossCreditSourceSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    // the satellite has a fee set-up whose accrual has started by needed_through
    required: z.boolean().nullish(),
    // LEAST(selected period end, the satellite's fee schedule end)
    needed_through: z.string().nullish(),
    last_booked_period_end: z.string().nullish(),
    ready: z.boolean().nullish()
  })
  .passthrough()

export const FeeBookingPositionFundSchema = z
  .object({
    fund_id: z.string(),
    fund_name: z.string().nullish(),
    fee_schedule_start: z.string().nullish(),
    // last day the fee periods cover; null on a flat-rate fund
    fee_schedule_end: z.string().nullish(),
    headline_rate: z.number().nullish(),
    frequency_label: z.string().nullish(),
    frequency_months: z.number().nullish(),
    fee_period_count: z.number().nullish(),
    has_fee_periods: z.boolean().nullish(),
    last_booked_period_end: z.string().nullish(),
    last_booked_transaction_id: z.string().nullish(),
    // set when this fund is the primary of a fee cross-credit group:
    // the satellite fund name(s) whose booked fees offset this fund's
    cross_credit_fund_names: z.string().nullish(),
    // booking order (owner 2026-09-29): per crediting satellite, whether it
    // has booked its fees through the period this fund is booking
    cross_credit_sources: z.array(FeeBookingCrossCreditSourceSchema).nullish(),
    // every crediting satellite ready (true when there are none): the
    // workbench disables Create fee transaction until it is
    cross_credit_ready: z.boolean().nullish(),
    // late-joiner fee reallocation (Origin SBIC LPA § 5.04(e)): the group
    // whose satellite reallocates fees to date, and the satellite admission
    // dates inside this fund's last billed period after its booking
    fee_reallocation_group_id: z.string().nullish(),
    fee_reallocation_group_name: z.string().nullish(),
    fee_reallocation_admissions: z.array(z.string()).nullish(),
    warning: z.string().nullish()
  })
  .passthrough()

export const FeeBookingPositionRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    prog__due_prior: z.number().nullish(),
    prog__cross_credit_prior: z.number().nullish(),
    prog__booked_prior: z.number().nullish(),
    prog__delta_prior: z.number().nullish(),
    prog__due_current: z.number().nullish(),
    prog__cross_credit_current: z.number().nullish(),
    prog__booked_current: z.number().nullish(),
    prog__delta_total: z.number().nullish(),
    // fees are booked on a commitment the fee engine does not price (not
    // Closed, or closed after the period): nothing is due, so the delta is
    // minus the booked fees and the next booking reverses them
    prog__outside_population: z.boolean().nullish()
  })
  .passthrough()

export const FeeBookingPositionSchema = z
  .object({
    fund: FeeBookingPositionFundSchema,
    rows: z.array(FeeBookingPositionRowSchema),
    unattributed_booked_prior: z.number().nullish(),
    unattributed_booked_current: z.number().nullish(),
    unattributed_cross_credit_prior: z.number().nullish(),
    unattributed_cross_credit_current: z.number().nullish()
  })
  .passthrough()

export type FeeBookingPositionFund = z.infer<typeof FeeBookingPositionFundSchema>
export type FeeBookingCrossCreditSource = z.infer<typeof FeeBookingCrossCreditSourceSchema>
export type FeeBookingPositionRow = z.infer<typeof FeeBookingPositionRowSchema>
export type FeeBookingPosition = z.infer<typeof FeeBookingPositionSchema>

export const validateFeeBookingPosition = (data: unknown): FeeBookingPosition =>
  validateWithZodSchema(FeeBookingPositionSchema, data, 'fee booking position')
